import CTALink from "@/components/CTA/ctaLink";
import EmphasizedText from "@/components/EmphasizedText";
import Icon from "@/components/icons";
import {
  ConversionTrackingInfo,
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { CTALinkProps } from "../CTA/variants/CtaLink/shared";
import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";
import getProgramFromSubject from "@/components/ProgramDetailLayout/templates/utils/getSubjectFromProgram";
import { getBrandString } from "@/utils/tagular/helpers";

interface BundleSavingsBannerProps {
  title?: string;
  titleEmphasis?: string;
  badge?: {
    text: string;
    icon?: string;
  };
  description?: React.ReactNode | string;
  discount?: string;
  cta?: CTALinkProps;
  ctaElement?: React.ReactNode;
  bulletPoints?: string[];
  theme?: "light" | "dark";
  compact?: boolean;
  forceMobileLayout?: boolean;
  partnerName?: string;
  programData?: Program;
  courses?: ProcessedCourse[];
  isEnrollmentUrl?: boolean;
}

export default function BundleSavingsBanner({
  title,
  titleEmphasis,
  badge,
  description,
  discount,
  cta,
  ctaElement,
  bulletPoints,
  theme = "dark",
  compact = false,
  forceMobileLayout = false,
  partnerName,
  programData,
  courses,
  isEnrollmentUrl,
}: BundleSavingsBannerProps) {
  const isLight = theme === "light";
  const { ref, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const viewedRef = useRef(false);

  const getBannerName = (location: string | undefined): string => {
    switch (location) {
      case "single-course-detail-page":
        return "single-course-bundling-banner";
      case "program-detail-page":
        return "program-bundling-banner";
      case "choose-your-track":
        return "choose-your-track-bundling-banner";
      default:
        return "bundling-banner";
    }
  };

  const bannerName = getBannerName(cta?.location);

  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Banner,
      htmlId: "bundling-banner",
      location: cta?.location || "",
      name: bannerName,
      text: "",
    },
  });

  const conversionTrackingInfo: ConversionTrackingInfo | undefined =
    isEnrollmentUrl && programData && courses && courses.length > 0
      ? {
          customName: "enroll",
          productList: courses.map((course) => {
            const programSubject = getProgramFromSubject(programData);
            const brand = getBrandString(programData);
            return {
              productName: course.productName || "unknown",
              brand,
              category: programSubject,
              variant: programData.productType,
              id: course.key,
              externalId: course.activeCourseRun?.key,
            };
          }),
        }
      : undefined;

  useEffect(() => {
    if (inView && !viewedRef.current) {
      elementViewed();
      viewedRef.current = true;
    }
  }, [inView, elementViewed]);

  return (
    <div
      ref={ref}
      className={`flex flex-col justify-around ${isLight ? "bg-putty-200" : "bg-primary"} rounded-xl ${forceMobileLayout ? "p-4 gap-8" : "p-4 md:p-8 gap-8 md:gap-6"} ${!compact && !forceMobileLayout ? "lg:gap-4 lg:flex-row" : ""}`}
    >
      <div
        className={`flex flex-col w-auto gap-6 ${forceMobileLayout ? "gap-6" : "md:gap-4"}`}
      >
        {badge && (
          <div>
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 ${isLight ? "bg-primary-500 text-white" : "bg-[#BEF4E5] text-primary-500"} text-xs font-bold rounded`}
            >
              {badge.icon && (
                <Icon type={badge.icon} svgProps={{ width: 14, height: 14 }} />
              )}
              {badge.text}
            </span>
          </div>
        )}
        {title && (
          <h2
            className={`${isLight ? "text-primary-500" : "text-white"} text-2xl ${forceMobileLayout ? "text-center" : "md:text-3xl"} m-0`}
          >
            <EmphasizedText
              text={
                partnerName
                  ? title.replace("{partnerName}", partnerName)
                  : title
              }
              textEmphasis={
                partnerName && titleEmphasis
                  ? titleEmphasis.replace("{partnerName}", partnerName)
                  : titleEmphasis
              }
              emphasisClass="text-brand-500"
            />
          </h2>
        )}
        {bulletPoints && bulletPoints.length > 0 && (
          <ul
            className={`flex flex-col ${forceMobileLayout ? "flex-col" : "md:flex-row md:flex-wrap"} gap-4 ${forceMobileLayout ? "gap-4" : "md:gap-5"} list-none !p-0`}
          >
            {bulletPoints.map((item, index) => (
              <li
                key={index}
                className={`${isLight ? "text-primary-500" : "text-white font-bold"} text-base ${!compact && !forceMobileLayout ? "lg:text-lg" : ""} flex gap-2`}
              >
                <div className="flex items-center justify-center">
                  <Icon
                    type="circle-check-outline"
                    svgProps={{ width: "20", height: "20" }}
                  />
                </div>
                {item}
              </li>
            ))}
          </ul>
        )}
        {description && (
          <p
            className={`text-md ${!forceMobileLayout ? "md:text-base" : ""} ${isLight ? "text-primary-500" : "text-white"} ${!compact && !forceMobileLayout ? "lg:text-lg" : ""} mb-0`}
          >
            {description}
          </p>
        )}
      </div>
      <div
        className={`border-b ${!compact && !forceMobileLayout ? "lg:border-r lg:border-b-0" : ""} ${isLight ? "border-putty-500" : "border-secondary-500"}`}
      ></div>
      <div
        className={`flex flex-col ${forceMobileLayout ? "flex-col" : "md:flex-row"} justify-center items-center gap-4 ${forceMobileLayout ? "gap-4" : "md:gap-6"} ${!compact && !forceMobileLayout ? "lg:gap-4 lg:flex-col lg:w-[343px]" : ""}`}
      >
        {discount && (
          <span
            className={`${isLight ? "text-primary-500" : "text-white"} text-2xl ${forceMobileLayout ? "text-2xl" : "md:text-3xl"} ${forceMobileLayout ? "" : "md:text-nowrap"} font-bold`}
          >
            Save {discount}
          </span>
        )}
        {ctaElement}
        {cta && !ctaElement && (
          <CTALink
            type="brand"
            size="xl"
            className="w-full"
            enable_tracking={true}
            label={cta.label || ""}
            url={cta.url}
            location={cta.location}
            html_id={cta.html_id}
            name={cta.name || ""}
            conversionTrackingInfo={conversionTrackingInfo}
          />
        )}
      </div>
    </div>
  );
}
